<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import BuzzMentionDomainCell from './BuzzMentionDomainCell.vue';

const props = defineProps({
  media: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const directoryLink = site => {
  if (!site) return '';
  if (site.startsWith('http://') || site.startsWith('https://')) return site;
  return `https://${site}`;
};

const tableData = computed(() =>
  (props.media?.directory_details || [])
    .filter(listing => listing?.site && Number(listing.count) > 0)
    .map(listing => ({
      id: listing.site,
      site: listing.site,
      link: directoryLink(listing.site),
      count: listing.count,
    }))
);

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('site', {
    header: t('BUZZ_REPORTS.TABLE.DIRECTORY'),
    size: 280,
    cell: cellProps => {
      const row = cellProps.row.original;
      return h(BuzzMentionDomainCell, {
        domain: row.site,
        link: row.link,
      });
    },
  }),
  columnHelper.accessor('count', {
    header: t('BUZZ_REPORTS.TABLE.LISTINGS'),
    size: 120,
    cell: cellProps =>
      h(
        'span',
        { class: 'text-n-slate-12 font-medium tabular-nums' },
        cellProps.getValue()
      ),
  }),
];

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: {
      pageSize: 10,
    },
  },
});
</script>

<template>
  <p v-if="!tableData.length" class="text-sm text-n-slate-10 mb-0">
    {{ t('BUZZ_REPORTS.INSIGHTS.NO_DIRECTORIES') }}
  </p>
  <div v-else class="overflow-x-auto">
    <Table :table="table" />
    <Pagination class="px-5 py-3 print:hidden" :table="table" />
  </div>
</template>
